"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Check, Loader2, Calendar, MapPin, Phone, Mail, User, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="21414" className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-slate-800/50 rounded-xl border border-slate-700 animate-in fade-in zoom-in duration-300">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center text-primary mb-2">
          <Check className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-slate-50">Inspection Scheduled!</h3>
        <p className="text-slate-400 max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary text-slate-50" onClick={() => window.location.reload()}>
          Book New Inspection
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-50/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-slate-500" />
                  <Input placeholder="Jane Smith" className="pl-9 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary text-slate-900 placeholder:text-slate-400" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-50/90 font-medium">Contact Email</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-slate-500" />
                  <Input type="email" placeholder="info@trusted-fl.com" className="pl-9 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary text-slate-900 placeholder:text-slate-400" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-50/90 font-medium">Contact Phone</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-slate-500" />
                  <Input type="tel" placeholder="786-382-6895" className="pl-9 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary text-slate-900 placeholder:text-slate-400" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_date" rules={{
        required: "Please select a preferred date"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-50/90 font-medium">Inspection Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-slate-500" /></SafeHydrate>
                  <Input type="date" className="pl-9 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary text-slate-900 block w-full" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="property_address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-50/90 font-medium">Property to Inspect <span className="text-slate-400 text-xs font-normal ml-1">(Required)</span></FormLabel>
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-3 top-2.5 size-4 text-slate-500" />
                <Input placeholder="456 Oak Avenue, Toronto, ON" className="pl-9 bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary text-slate-900 placeholder:text-slate-400" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-50/90 font-medium">Specific Concerns <span className="text-slate-400 text-xs font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <div className="relative">
                <FileText className="absolute left-3 top-3 size-4 text-slate-500" />
                <textarea className="flex min-h-[100px] w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 pl-9 text-sm text-slate-900 shadow-inner placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50" placeholder="Tell us about any specific areas of concern or property details..." {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[6px] shadow-lg shadow-primary/20 transition-all hover:scale-[1.01] uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Booking...
          </> : "Book Your Inspection"}
      </Button>
      
      <p className="text-center text-xs text-slate-400 mt-4">
        Expert eyes. Total peace of mind. We protect your data.
      </p>
    </Form>;
}
export default function ScheduleAFreeConsultation() {
  const benefits = [{
    title: "Structural Integrity Check",
    description: "Comprehensive review of the foundation, framing, and load-bearing elements."
  }, {
    title: "Electrical & Plumbing Audit",
    description: "Detailed assessment of all systems for safety and current code compliance."
  }, {
    title: "HVAC Performance Report",
    description: "Efficiency testing and lifespan estimation for heating and cooling units."
  }, {
    title: "Moisture & Mold Detection",
    description: "Advanced thermal imaging to find hidden leaks and potential health hazards."
  }, {
    title: "Digital Summary Report",
    description: "Easy-to-read technical breakdown delivered within 24 hours of inspection."
  }];
  return <section id="final-calltoaction" className="dark bg-slate-900 text-slate-50 py-20 lg:py-28 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      </div>
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-slate-700/20 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="space-y-10 lg:sticky lg:top-24">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-bold uppercase tracking-widest">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Expert Property Analysis
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-slate-50 leading-[1.1]">
                Uncover the truth before you <span className="text-primary">commit.</span>
              </h2>
              
              <p className="text-lg text-slate-400 leading-relaxed max-w-xl">
                Book your professional inspection now. We provide the detailed technical insight and expert "Big Sister" perspective you need to make an informed decision.
              </p>
            </div>

            <div className="space-y-8">
              <h3 className="text-xl font-bold text-slate-50 border-b border-slate-700 pb-4 inline-block uppercase tracking-wide">
                What's included in your inspection:
              </h3>
              
              <ul className="space-y-6">
                {benefits.map((item, i) => <li key={i} data-index={i} className="flex gap-4 group">
                    <div className="flex-shrink-0 mt-1">
                      <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                        <Check className="size-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-50 text-lg group-hover:text-primary transition-colors duration-300">
                        {item.title}
                      </h4>
                      <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="w-full">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl shadow-black/40 p-6 md:p-8 lg:p-10 relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-orange-400 to-primary opacity-90" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-50 mb-2">Book Your Inspection</h3>
                <p className="text-slate-400 text-sm">
                  Complete the form and our technical team will contact you within 24 hours to finalize your appointment.
                </p>
              </div>

              <FormProvider formId="schedule_consultation_form_sell" sectionName="sell-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              preferred_date: "",
              property_address: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              preferred_date: {
                required: true
              }
            }} submitText="Book Your Inspection" successMessage="Thank you! Your inspection request has been received. Our team will contact you shortly to confirm the technical details.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}